"use client";

import {
  BellRing,
  Check,
  FileText,
  LayoutDashboard,
  Mail,
  MessageSquareText,
  SearchCheck,
  ShoppingCart,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { WebsiteCtaLink } from "@/app/(website)/_components/website-cta-link";

type ProductEngine = {
  title: string;
  description: string;
  icon: LucideIcon;
};

type ScheduledBrief = {
  label: string;
  cadence: string;
  description: string;
};

const productEngines: ProductEngine[] = [
  {
    title: "Screenshot report",
    description: "Scores CTA visibility, pricing prominence, trust signals, and page clarity.",
    icon: Sparkles,
  },
  {
    title: "Competitor report",
    description: "Compares pricing, trust, availability, and positioning across discovered competitors.",
    icon: SearchCheck,
  },
  {
    title: "Purchase-flow test",
    description: "Validates add-to-cart, cart persistence, checkout filling, and payment readiness.",
    icon: ShoppingCart,
  },
  {
    title: "Full audit",
    description: "Synthesizes the child reports into one linked product-health summary.",
    icon: FileText,
  },
];

const scheduledBriefs: ScheduledBrief[] = [
  {
    label: "Daily",
    cadence: "Every morning",
    description: "Flags what changed yesterday and the few issues worth fixing first.",
  },
  {
    label: "Weekly",
    cadence: "Every week",
    description: "Shows the pattern behind spend, conversion, and competitor movement.",
  },
  {
    label: "Monthly",
    cadence: "Every month",
    description: "Frames direction, priorities, and category momentum for the next decisions.",
  },
];

const deliveryChannels = [
  { label: "Email", icon: Mail },
  { label: "WhatsApp", icon: MessageSquareText },
  { label: "Dashboard", icon: LayoutDashboard },
];

export function ReportFeatureShowcase() {
  return (
    <>
      <section className="border-y border-slate-800/60 bg-slate-900/45 py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <ScrollReveal>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-cyan-300/80">Reports</p>
              <h2 className="mt-4 text-3xl font-bold text-white md:text-5xl">
                Two different report systems for two different decisions.
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-300 md:text-xl">
                Storita gives teams focused product intelligence when one page needs explanation, and recurring briefs that keep the whole store monitored over time.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="grid items-center gap-14 lg:grid-cols-[0.96fr_1.04fr]">
              <ScrollReveal className="order-2 lg:order-1">
                <div className="mx-auto max-w-[720px]">
                  <div className="relative overflow-hidden rounded-[2rem] border border-slate-800/80 bg-slate-950/85 p-4 shadow-[0_24px_64px_rgba(2,6,23,0.42)] md:p-5">
                    <div className="absolute right-[-5%] top-[-8%] h-28 w-28 rounded-full bg-cyan-400/10 blur-3xl" />
                    <div className="absolute left-[-4%] bottom-[-10%] h-28 w-28 rounded-full bg-emerald-400/10 blur-3xl" />

                    <div className="relative rounded-[1.5rem] border border-slate-800/80 bg-slate-900/80 p-4 md:p-5">
                      <div className="flex flex-wrap items-center gap-2 border-b border-slate-800/80 pb-4">
                        <span className="rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-100">
                          Example product intelligence
                        </span>
                        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-300">
                          Full audit summary
                        </span>
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        {[
                          ["Page readiness", "Visual conversion review"],
                          ["Market position", "Pricing and trust comparison"],
                          ["Checkout path", "Browser-validated flow"],
                        ].map(([label, value]) => (
                          <div key={label} className="rounded-[1.1rem] border border-slate-800/80 bg-slate-950/80 px-3 py-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
                            <p className="mt-2 text-sm font-semibold text-white">{value}</p>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 rounded-[1.3rem] border border-amber-300/20 bg-[linear-gradient(180deg,rgba(251,191,36,0.14),rgba(249,115,22,0.08),rgba(15,23,42,0.92))] p-4">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-100/90">Executive readout</p>
                        <p className="mt-3 text-base font-semibold leading-relaxed text-white">
                          One product can be reviewed from page clarity, market pressure, and checkout readiness in one linked analysis.
                        </p>
                      </div>

                      <div className="mt-4 space-y-3">
                        {productEngines.map((engine) => {
                          const Icon = engine.icon;

                          return (
                            <div
                              key={engine.title}
                              className="flex items-start gap-3 rounded-[1.2rem] border border-slate-800/75 bg-slate-950/72 px-3 py-3"
                            >
                              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-slate-100">
                                <Icon className="h-4.5 w-4.5" />
                              </span>
                              <div>
                                <p className="text-sm font-semibold text-white">{engine.title}</p>
                                <p className="mt-1 text-sm leading-relaxed text-slate-300">{engine.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={120} className="order-1 lg:order-2">
                <div className="max-w-2xl text-center lg:text-left">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">Product intelligence</p>
                  <h3 className="mt-3 text-3xl font-semibold text-white md:text-5xl">
                    Focused analysis for one product, one page, or one checkout path.
                  </h3>
                  <p className="mt-5 text-base leading-relaxed text-slate-300 md:text-lg">
                    Product intelligence opens the right analysis engine for the question at hand. It reviews a page visually, compares the offer against the market, validates the purchase flow in a real browser session, and combines those findings into one full audit.
                  </p>

                  <div className="mt-8 space-y-3 text-left">
                    {[
                      "Screenshot reports highlight CTA visibility, pricing prominence, trust signals, and visual friction.",
                      "Competitor reports compare price, availability, reviews, messaging, and market position.",
                      "Purchase-flow tests verify add-to-cart, cart persistence, checkout progression, and payment readiness.",
                      "Full audits connect the child reports into one product-health summary with linked evidence.",
                    ].map((bullet) => (
                      <div key={bullet} className="flex items-start gap-3 rounded-2xl border border-slate-800/75 bg-slate-900/45 px-4 py-3 text-sm leading-relaxed text-slate-200 md:text-[15px]">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                        <span>{bullet}</span>
                      </div>
                    ))}
                  </div>

                  <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-400 md:text-[15px]">
                    Use this when one product or landing page needs a clear explanation before the team changes pricing, design, or merchandising.
                  </p>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-800/50 bg-slate-900/35 py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="grid items-center gap-14 lg:grid-cols-[1.02fr_0.98fr]">
              <ScrollReveal>
                <div className="max-w-2xl text-center lg:text-left">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-fuchsia-200">Scheduled briefs</p>
                  <h3 className="mt-3 text-3xl font-semibold text-white md:text-5xl">
                    Daily, weekly, and monthly reviews that keep the store monitored.
                  </h3>
                  <p className="mt-5 text-base leading-relaxed text-slate-300 md:text-lg">
                    These briefs turn store performance, traffic, customer behavior, and competitor movement into a rhythm the team can actually use. Each cadence answers a different decision horizon.
                  </p>

                  <div className="mt-8 grid gap-3 text-left">
                    {scheduledBriefs.map((brief) => (
                      <div key={brief.label} className="rounded-[1.2rem] border border-slate-800/75 bg-slate-950/50 px-4 py-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-white">{brief.label} brief</p>
                          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-300">
                            {brief.cadence}
                          </span>
                        </div>
                        <p className="mt-3 text-sm leading-relaxed text-slate-300 md:text-[15px]">{brief.description}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex flex-wrap justify-center gap-2 lg:justify-start">
                    {deliveryChannels.map((channel) => {
                      const Icon = channel.icon;

                      return (
                        <span key={channel.label} className="inline-flex items-center gap-2 rounded-full border border-slate-700/80 bg-slate-900/80 px-3 py-2 text-xs font-medium text-slate-200">
                          <Icon className="h-3.5 w-3.5 text-slate-400" />
                          {channel.label}
                        </span>
                      );
                    })}
                  </div>

                  <div className="mt-6">
                    <WebsiteCtaLink href="/login" className="w-full justify-center sm:w-auto">
                      Start monitoring
                    </WebsiteCtaLink>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={120}>
                <div className="mx-auto max-w-[720px]">
                  <div className="relative overflow-hidden rounded-[2rem] border border-slate-800/80 bg-slate-950/85 p-4 shadow-[0_24px_64px_rgba(2,6,23,0.42)] md:p-5">
                    <div className="absolute left-[-5%] top-[-8%] h-28 w-28 rounded-full bg-fuchsia-400/10 blur-3xl" />
                    <div className="absolute right-[-4%] bottom-[-10%] h-28 w-28 rounded-full bg-cyan-400/10 blur-3xl" />

                    <div className="relative rounded-[1.5rem] border border-slate-800/80 bg-slate-900/80 p-4 md:p-5">
                      <div className="flex flex-wrap items-center gap-2 border-b border-slate-800/80 pb-4">
                        <span className="rounded-full border border-fuchsia-300/20 bg-fuchsia-300/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-fuchsia-100">
                          Example daily brief
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-300">
                          <BellRing className="h-3.5 w-3.5" />
                          Sent 7:00 AM
                        </span>
                      </div>

                      <div className="mt-4 rounded-[1.3rem] border border-slate-800/80 bg-slate-950/82 p-4">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Headline</p>
                        <p className="mt-3 text-lg font-semibold leading-relaxed text-white">
                          Revenue held up, but two hero products need mobile conversion attention before noon.
                        </p>
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        {[
                          ["Revenue", "$18.9k"],
                          ["Conversion", "2.9%"],
                          ["Priority", "2 products"],
                        ].map(([label, value]) => (
                          <div key={label} className="rounded-[1.1rem] border border-slate-800/80 bg-slate-950/80 px-3 py-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
                            <p className="mt-2 text-sm font-semibold text-white">{value}</p>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
                        <div className="rounded-[1.25rem] border border-slate-800/80 bg-slate-950/72 p-4">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">What lands in the brief</p>
                          <div className="mt-4 space-y-3">
                            {[
                              "What changed across revenue, products, and traffic",
                              "Which competitor movement matters today",
                              "One recommended move the team can act on first",
                            ].map((item) => (
                              <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-800/70 bg-slate-900/75 px-3 py-3 text-sm text-slate-200">
                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="rounded-[1.25rem] border border-amber-300/20 bg-[linear-gradient(180deg,rgba(251,191,36,0.14),rgba(249,115,22,0.08),rgba(15,23,42,0.92))] p-4">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-100/90">Recommended move</p>
                          <p className="mt-3 text-base font-semibold leading-relaxed text-white">
                            Review the two highest-traffic products with weaker mobile conversion before the next campaign push.
                          </p>
                          <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 px-3 py-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Cadence logic</p>
                            <p className="mt-2 text-sm leading-relaxed text-slate-200">
                              Daily catches change, weekly shows the pattern, and monthly helps the team choose direction.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}